<template>
  <div class="type-analysis">
    <MysticalCard class="type-card">
      <div class="type-header">
        <h2 class="type-code">{{ type }}</h2>
        <div class="type-title">
          <h3>{{ personalityType.name }}</h3>
          <p class="type-subtitle">{{ personalityType.title }}</p>
        </div>
      </div>

      <div class="type-content">
        <div class="description-card">
          <h4>性格特点</h4>
          <p>{{ personalityType.description }}</p>
        </div>

        <div class="traits-grid">
          <div class="strengths-card">
            <h4>优势特质</h4>
            <ul>
              <li 
                v-for="strength in personalityType.strengths" 
                :key="strength"
                class="trait-item"
              >
                <div class="section-icon">
                  <Icon name="Plus" class="icon" />
                </div>
                {{ strength }}
              </li>
            </ul>
          </div>

          <div class="weaknesses-card">
            <h4>发展方向</h4>
            <ul>
              <li 
                v-for="weakness in personalityType.weaknesses" 
                :key="weakness"
                class="trait-item"
              >
                <div class="section-icon">
                  <Icon name="Warning" class="icon" />
                </div>
                {{ weakness }}
              </li>
            </ul>
          </div>

          <div class="careers-card">
            <h4>适合的职业</h4>
            <ul>
              <li 
                v-for="career in personalityType.careers" 
                :key="career"
                class="trait-item"
              >
                <div class="section-icon">
                  <Icon name="Work" class="icon" />
                </div>
                {{ career }}
              </li>
            </ul>
          </div>
        </div>
      </div>
    </MysticalCard>

    <MysticalCard class="dimensions-card">
      <h4>维度分析</h4>
      <div class="dimensions-grid">
        <div 
          v-for="(letter, index) in type" 
          :key="index"
          class="dimension-item"
        >
          <div class="dimension-header">
            <h5>{{ getDimensionName(letter) }}</h5>
            <span class="dimension-label">{{ getDimensionLabel(letter) }}</span>
          </div>
          <p class="dimension-traits">{{ getDimensionDescription(letter) }}</p>
        </div>
      </div>
    </MysticalCard>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { MBTI } from '@/data/mbti'
import MysticalCard from '@/components/common/MysticalCard.vue'
import Icon from '@/components/common/Icon.vue'

const props = defineProps({
  type: {
    type: String,
    required: true
  }
})

const typeInfo = computed(() => MBTI.types[props.type])

const personalityType = computed(() => ({
  name: typeInfo.value.name,
  title: typeInfo.value.title,
  description: typeInfo.value.description,
  strengths: typeInfo.value.strengths,
  weaknesses: typeInfo.value.weaknesses,
  careers: typeInfo.value.careers
}))

const getDimensionName = (letter) => {
  const dimensionMap = {
    'E': '外向', 'I': '内向',
    'S': '感觉', 'N': '直觉',
    'T': '思考', 'F': '情感',
    'J': '判断', 'P': '知觉'
  }
  return dimensionMap[letter] || letter
}

const getDimensionLabel = (letter) => {
  const labelMap = {
    'E': '从外部世界获取能量',
    'I': '从内部世界获取能量',
    'S': '关注具体和细节',
    'N': '关注概念和可能性',
    'T': '基于逻辑做决定',
    'F': '基于价值观做决定',
    'J': '倾向计划和组织',
    'P': '倾向灵活和适应'
  }
  return labelMap[letter] || ''
}

const getDimensionDescription = (letter) => {
  return MBTI.dimensionTraits[letter]?.traits.join('，') || ''
}
</script>

<style scoped>
.type-analysis {
  max-width: 800px;
  margin: 0 auto;
}

.type-card {
  padding: var(--spacing-lg);
  box-shadow: var(--shadow-normal);
}

.type-header {
  display: flex;
  align-items: center;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-xl);
}

.type-code {
  font-size: 2.5rem;
  color: var(--color-accent);
  font-weight: bold;
}

.type-title {
  h3 {
    font-size: 1.8rem;
    margin-bottom: var(--spacing-xs);
  }

  .type-subtitle {
    color: var(--color-text-light);
    font-size: 1.1rem;
  }
}

.description-card {
  margin-bottom: var(--spacing-xl);
  
  h4 {
    color: var(--color-accent);
    margin-bottom: var(--spacing-md);
  }
  
  p {
    line-height: 1.6;
  }
}

.traits-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--spacing-lg);
}

.trait-item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-sm);
  
  .material-icons {
    color: var(--color-accent);
    font-size: 1.2rem;
  }
}

.dimensions-card {
  margin-top: var(--spacing-xl);
}

.dimensions-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: var(--spacing-lg);
  margin-top: var(--spacing-md);
}

.dimension-item {
  background: var(--color-bg-light);
  padding: var(--spacing-md);
  border-radius: var(--radius-md);
}

.dimension-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--spacing-sm);
  
  h5 {
    color: var(--color-accent);
    font-size: 1.1rem;
  }
  
  .dimension-label {
    font-size: 0.9rem;
    color: var(--color-text-light);
  }
}

.dimension-traits {
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--color-text);
}

@media (max-width: 768px) {
  .type-header {
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-md);
  }
}

.section-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--color-primary);
  margin-right: var(--spacing-md);
}

.icon {
  font-size: 1.5rem;
  color: var(--color-light);
}
</style> 